/**
 * SE2 · Cart line purchase-mode toggle, in the drawer and on /cart.
 * Figma o2LnJa3tlbPwKEorUzEDKv 564:31 ("subscription-toggle" inside product-item 564:23).
 *
 * Switches a cart line between one-time and a subscription selling plan. The line is changed through
 * Shopify's own /cart/change.js with `selling_plan`, requesting the sections of every cart view on the page in
 * the same call (the closest <cart-items-component> first: the drawer, or on /cart the cart section and its
 * suggestions; CART-B-10, 2026-10-05). The response's section HTML is handed to the theme by resolving the
 * promise carried on a CartLinesUpdateEvent — the identical contract component-cart-items.js uses for a quantity
 * change — so each <cart-items-component> morphs its own section and nothing is re-rendered by hand.
 *
 * Announcing the switch: in the drawer the status script in snippets/cart-drawer.liquid does it (inside the
 * modal dialog). On /cart, where the drawer is not rendered, this module writes "<product>: switched to <plan>"
 * into a polite status region it adds to <body>, outside every morphed section.
 *
 * No JavaScript: the toggle is not rendered at all (it is emitted only inside <se2-line-plan>, which
 * does nothing until this module upgrades it), so a line simply keeps whatever plan it was added with.
 *
 * A refused switch (V-cart-06, 2026-10-06) is shown on the line: Shopify's own description of the refusal, or the
 * toggle's data-error text when there is none (a network failure), goes into the row's error cell, whose
 * role="alert" announces it. The pill stays as it was, which is still the truth.
 *
 * KV-01 (2026-10-05, kit build finding): the kit's one-time gift label ("Free on Subscribe & save, then ...") is
 * printed hidden by snippets/cart-products.liquid with data-se2-gift-once and data-se2-gift-code. It is only true
 * while that code is still on the cart, which Liquid cannot see when the code does not apply. The check at the
 * end of this file reveals it only when GET /cart.js lists the code in discount_codes (any case, applicable or
 * not), and keeps it hidden whenever the answer is missing (fails closed).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const MAX_SECTIONS = 5; // the Section Rendering API's limit per request
const FALLBACK_ERROR = 'The purchase option could not be changed just now. Please try again.';

/** Section ids of every cart view on the page, the one holding `element` first. */
function cartSectionIds(element) {
  const ids = [];
  const add = (node) => {
    const id = node instanceof HTMLElement ? node.dataset.sectionId : '';
    if (id && !ids.includes(id)) ids.push(id);
  };
  add(element.closest('cart-items-component[data-section-id]'));
  document.querySelectorAll('cart-items-component[data-section-id]').forEach(add);
  if (!ids.length) ids.push('cart-drawer-section');
  return ids.slice(0, MAX_SECTIONS);
}

/** The /cart status region: created once, outside the morphed sections, so a re-render never wipes it. */
function pageStatusRegion() {
  let region = document.querySelector('[data-se2-plan-status-page]');
  if (!region) {
    region = document.createElement('p');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('data-se2-plan-status-page', '');
    document.body.append(region);
  }
  return region;
}

class Se2LinePlan extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
    this.dataset.ready = '1';
    // Outside the drawer the region has to exist before it is written to, or it may not be announced.
    if (!this.closest('#cart-drawer')) pageStatusRegion();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-se2-plan]') : null;
    if (!button || button.getAttribute('aria-pressed') === 'true' || this.hasAttribute('busy')) return;
    event.preventDefault();
    let announcement = '';
    if (!this.closest('#cart-drawer')) {
      const title = this.closest('.cart-items__table-row')?.querySelector('.cart-items__title');
      announcement = `${(title?.textContent || '').trim()}: switched to ${button.textContent.trim()}`;
    }
    this.#change(button.dataset.se2Plan || null, announcement);
  };

  /** The row's error cell (snippets/cart-products), shared with the quantity errors of assets/component-cart-items.js. */
  #errorParts() {
    const row = this.closest('.cart-items__table-row');
    const cell = row?.querySelector('.cart-items__error');
    const text = row?.querySelector('.cart-item__error-text');
    return cell instanceof HTMLElement && text instanceof HTMLElement ? { cell, text } : null;
  }

  /** @param {string} message */
  #showError(message) {
    const parts = this.#errorParts();
    if (!parts) return;
    parts.text.textContent = '';
    parts.cell.classList.remove('hidden');
    // Written a beat after the cell shows, so the role="alert" inside it is announced with its text. Then scrolled
    // into view: in the drawer the scroller's scroll padding keeps it above the pinned checkout bar (V-cart-08, fix
    // round 1; snippets/cart-drawer).
    setTimeout(() => {
      parts.text.textContent = message;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      parts.cell.scrollIntoView({ block: 'nearest', behavior: reduce ? 'instant' : 'smooth' });
    }, 50);
  }

  #clearError() {
    const parts = this.#errorParts();
    if (!parts || parts.cell.classList.contains('hidden')) return;
    parts.cell.classList.add('hidden');
    parts.text.textContent = '';
  }

  async #change(sellingPlan, announcement) {
    const key = this.dataset.lineKey;
    const quantity = Number(this.dataset.lineQuantity || '1');
    if (!key) return;

    this.#clearError();
    this.setAttribute('busy', '');
    const sectionIds = cartSectionIds(this);
    const changeUrl =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) || '/cart/change.js';

    // Announce the update first so the theme can put the cart into its loading state, exactly as a
    // quantity change does, then resolve that promise with the freshly rendered sections.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          selling_plan: sellingPlan ? Number(sellingPlan) : null,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) {
        // Shopify explains a refused change in the body ({ status, message, description }); keep it for the line.
        let description = '';
        try {
          const refusal = await response.json();
          description = String((refusal && (refusal.description || refusal.message)) || '');
        } catch (parseError) {
          description = '';
        }
        throw Object.assign(new Error(`Cart change failed: ${response.status}`), { description });
      }
      const json = await response.json();
      // itemCount and items as component-cart-items.js sends them: the header cart icon reads
      // `cart?.totalQuantity ?? detail?.itemCount ?? 0`, so without a count it hid its bubble after every switch.
      deferred.resolve({ detail: { sections: json.sections, items: json.items, itemCount: json.item_count } });
      if (announcement) {
        const region = pageStatusRegion();
        // Cleared first, so toggling back and forth announces the same text again.
        region.textContent = '';
        setTimeout(() => {
          region.textContent = announcement;
        }, 50);
      }
    } catch (error) {
      console.error('[se2-line-plan]', error);
      deferred.reject(error);
      this.removeAttribute('busy');
      const description = error && typeof error === 'object' && 'description' in error ? String(error.description) : '';
      this.#showError(description || this.dataset.error || FALLBACK_ERROR);
    }
  }
}

if (!customElements.get('se2-line-plan')) {
  customElements.define('se2-line-plan', Se2LinePlan);
}

/* ---------- KV-01: the one-time gift label shows only while its code is on the cart ---------- */
const GIFT_ONCE = '[data-se2-gift-once]';

if (!window.__se2GiftOnce) {
  window.__se2GiftOnce = true;

  /** What this module last decided for each label, so its own hidden flips do not trigger another check. */
  const decided = new WeakMap();
  let timer = 0;
  let run = 0;

  const cartUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root.replace(/\/?$/, '/')}cart.js`;
  };

  const apply = (codes) => {
    document.querySelectorAll(GIFT_ONCE).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const code = (node.dataset.se2GiftCode || '').trim().toUpperCase();
      const show = Boolean(codes && code && codes.includes(code));
      decided.set(node, !show);
      if (node.hidden === show) node.hidden = !show;
    });
  };

  const check = async () => {
    if (!document.querySelector(GIFT_ONCE)) return;
    const mine = ++run;
    let codes = null;
    try {
      const response = await fetch(cartUrl(), { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        codes = (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).map((entry) =>
          String((entry && entry.code) || '')
            .trim()
            .toUpperCase()
        );
      }
    } catch (error) {
      codes = null;
    }
    if (mine !== run) return; // a newer check is on its way
    apply(codes);
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(check, 120);
  };

  const relevant = (mutation) => {
    if (mutation.type === 'attributes') {
      const target = mutation.target;
      return target instanceof HTMLElement && target.matches(GIFT_ONCE) && decided.get(target) !== target.hidden;
    }
    return Array.from(mutation.addedNodes).some(
      (node) => node instanceof Element && (node.matches(GIFT_ONCE) || node.querySelector(GIFT_ONCE))
    );
  };

  new MutationObserver((mutations) => {
    if (mutations.some(relevant)) schedule();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });

  schedule();
}
